// FastAPI/HTTPException `detail` alanını okunabilir tek satır mesaja çevirir.
//
// Neden var: backend bazı 400'lerde `detail`'i bir DICT döndürür (ör. üretim
// yetersiz-stok: {hata, yetersiz:[...], ipucu}) veya 422'de bir ARRAY döndürür.
// Frontend `throw new Error(detail)` / `toast.error(detail)` yaptığında dict/array
// "[object Object]" olarak görünür (kullanıcı için anlamsız). Bu yardımcı her
// şekli (string | dict | array) güvenle okunur metne çevirir; ASLA "[object Object]"
// üretmez. Tüm sayfalarda yeniden kullanılabilir (utils/logError ile aynı ruh).

const _isObj = (v) => v != null && typeof v === 'object';

// Miktarı gereksiz ondalık sıfır olmadan yazar (5.0→"5", 0.1→"0.1", 2.55→"2.55").
const _miktar = (n) => {
  if (n == null || n === '') return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return String(+num.toFixed(2));
};

/**
 * @param {*} detail  HTTPException.detail (string | object | array | null)
 * @param {string} [fallback] detail boş/çözümlenemezse gösterilecek metin
 * @returns {string} kullanıcıya gösterilebilir tek satır mesaj
 */
export function extractApiError(detail, fallback = 'Bir hata oluştu') {
  if (detail == null) return fallback;

  if (typeof detail === 'string') {
    return detail.trim() || fallback;
  }

  // FastAPI 422 doğrulama hatası: [{loc, msg, type}, ...] veya düz string array
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (d == null) return '';
        if (!_isObj(d)) return String(d);
        // msg/message/detail string DEĞİLSE atla — aksi halde "[object Object]" sızar.
        const m = d.msg || d.message || d.detail;
        return typeof m === 'string' ? m : '';
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : fallback;
  }

  if (_isObj(detail)) {
    // Üretim yetersiz-stok şekli: hangi malzemenin eksik olduğunu açıkça listele.
    // (Teknik 'ipucu' / force=true son kullanıcıya bilinçli olarak gösterilmez.)
    if (Array.isArray(detail.yetersiz)) {
      const head = (typeof detail.hata === 'string' && detail.hata) || 'Yetersiz stok';
      const list = detail.yetersiz
        .map((y) => {
          if (!_isObj(y)) return y == null ? '' : String(y);
          const ad = y.malzeme ?? y.ad ?? y.malzeme_ad ?? 'Malzeme';
          // Miktar bilgisi yoksa yarım cümle ("mevcut  < gerekli ") yazma — sadece adı göster.
          const hasNums = Number.isFinite(Number(y.mevcut)) && Number.isFinite(Number(y.gerekli));
          return hasNums ? `${ad} (mevcut ${_miktar(y.mevcut)} < gerekli ${_miktar(y.gerekli)})` : String(ad);
        })
        .filter(Boolean);
      return list.length ? `${head}: ${list.join(', ')}` : head;
    }

    // Bilinen mesaj anahtarları
    const known = detail.hata || detail.detail || detail.message || detail.msg || detail.error;
    if (typeof known === 'string' && known.trim()) return known.trim();

    // Son çare: skaler alanları "anahtar: değer" olarak birleştir ({} basma)
    const pairs = Object.entries(detail)
      .filter(([, v]) => v != null && typeof v !== 'object')
      .map(([k, v]) => `${k}: ${v}`);
    if (pairs.length) return pairs.join(', ');

    return fallback;
  }

  return String(detail) || fallback;
}

export default extractApiError;
